import { jwtDecode } from 'jwt-decode';

// Increased buffer time to 10 minutes to allow more retry attempts during network/server errors
const REFRESH_BEFORE_MINUTES = 10;

interface JwtPayload {
    exp: number;
    [key: string]: unknown;
}

/**
 * Decode JWT token and return the payload
 */
export const decodeToken = <T extends JwtPayload = JwtPayload>(token: string): T => {
    try {
        return jwtDecode<T>(token);
    } catch (error) {
        throw new Error(`Failed to decode token: ${error}`);
    }
};

/**
 * Get expiration time from JWT token in milliseconds
 */
export const getTokenExpirationTime = (token: string): number => {
    const decoded = decodeToken<{ exp: number }>(token);
    return decoded.exp * 1000; // Convert to milliseconds
};

/**
 * Calculate time until token expiration in milliseconds
 */
export const getTimeUntilExpiration = (token: string): number => {
    const expirationTime = getTokenExpirationTime(token);
    const currentTime = Date.now();
    return expirationTime - currentTime;
};

/**
 * Calculate refresh time (5 minutes before expiration) in milliseconds
 * @param token - JWT token string
 * @param refreshBeforeMinutes - Minutes before expiration to refresh (default: 5)
 * @returns Time in milliseconds until refresh should happen, or negative if should refresh immediately
 */
export const calculateTokenRefreshTime = (
    token: string,
    refreshBeforeMinutes: number = REFRESH_BEFORE_MINUTES
): number => {
    const timeUntilExpiration = getTimeUntilExpiration(token);
    const refreshTime = timeUntilExpiration - (refreshBeforeMinutes * 60 * 1000);
    return refreshTime;
};

/**
 * Check if token should be refreshed immediately
 */
export const shouldRefreshTokenImmediately = (token: string, refreshBeforeMinutes: number = REFRESH_BEFORE_MINUTES): boolean => {
    const refreshTime = calculateTokenRefreshTime(token, refreshBeforeMinutes);
    return refreshTime <= 0;
};

/**
 * Get token expiration information
 */
export const getTokenExpirationInfo = (token: string) => {
    const expirationTime = getTokenExpirationTime(token);
    const currentTime = Date.now();
    const timeUntilExpiration = expirationTime - currentTime;
    const refreshTime = calculateTokenRefreshTime(token, REFRESH_BEFORE_MINUTES);

    return {
        expirationTime,
        currentTime,
        timeUntilExpiration,
        refreshTime,
        shouldRefreshImmediately: refreshTime <= 0,
        minutesUntilExpiration: Math.round(timeUntilExpiration / 1000 / 60),
        minutesUntilRefresh: refreshTime > 0 ? Math.round(refreshTime / 1000 / 60) : 0,
    };
};

